import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { LeaguePreviewCard } from '@dreamteam-monorepo/ui';
import { useLeaguesStore } from '@/stores/LeaguesStore';
import { useAuthStore } from '@/stores/AuthStore';
import {
  leagueToCardProps,
  getClubBadge,
  getCountryFlag,
  orderLeaguesForDisplay,
} from '@/lib/leagueMappers';
import { GlobalLeaderboardCard } from './GlobalLeaderboardCard';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';

const SkeletonCard: FC = () => (
  <div className="h-[88px] rounded-[--corner-md] bg-gray-100 animate-pulse" />
);

const CommissionerLeagueArtwork: FC = () => (
  <picture className="block h-full w-full">
    <source media="(min-width: 640px)" srcSet="/leagues/commissioner-league-desktop.png" />
    <img
      src="/leagues/commissioner-league-mobile.png"
      alt=""
      className="h-full w-full object-cover"
    />
  </picture>
);

export const LeagueStack: FC = () => {
  const navigate = useNavigate();
  const sessionState = useAuthStore(s => s.sessionState);
  const { leagues, loading, fetchLeagues } = useLeaguesStore();
  const isAuthenticated = sessionState === 'authenticated';
  const orderedLeagues = orderLeaguesForDisplay(leagues);

  useEffect(() => {
    if (isAuthenticated) fetchLeagues();
  }, [isAuthenticated, fetchLeagues]);

  if (loading) {
    return (
      <div className="flex flex-col gap-[--padding-lg] mx-auto w-full">
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[--padding-lg] mx-auto w-full">
      <GlobalLeaderboardCard />
      {orderedLeagues.map(league => {
        const imageSrc = getClubBadge(league.club_id) ?? getCountryFlag(league.country_code);
        const isCommissionerLeague = league.commissioner_user_id !== null;
        return (
          <LeaguePreviewCard
            key={league.id}
            {...leagueToCardProps(league)}
            image={
              isCommissionerLeague ? (
                <CommissionerLeagueArtwork />
              ) : imageSrc ? (
                <img src={imageSrc} alt={league.name} className="w-20 h-20 object-contain" />
              ) : undefined
            }
            imagePanelBg={
              isCommissionerLeague
                ? 'bg-[#E3262E]'
                : league.type === 'national'
                  ? 'bg-[#F7FCFF]'
                  : undefined
            }
            onViewLeague={() => {
              trackFantasyEvent('leagueLink', { link_name: 'View League' });
              navigate(`/leagues/${league.id}`);
            }}
            className="shadow-[0px_4px_24px_0px_rgba(34,37,38,0.12)]"
          />
        );
      })}
    </div>
  );
};
